import React from 'react';
import { motion } from 'framer-motion';
export interface PlaceValueBlocksProps {
  /**
   * F-PVB-TENTHOUSANDS (mei 2026): tenThousands column added so 5-digit
   * numbers like 78.432 can be represented faithfully. Without this, seeds
   * had to drop the leading digit, which broke questions asking about the
   * value of a digit in the tienduizendtal-plek.
   */
  tenThousands?: number;
  thousands?: number;
  hundreds?: number;
  tens?: number;
  ones?: number;
  showLabels?: boolean;
  showTotal?: boolean;
  highlightColumn?: 'tenThousands' | 'thousands' | 'hundreds' | 'tens' | 'ones' | null;
  maxBlocksPerColumn?: number;
  size?: 'sm' | 'md' | 'lg';
}
type ColumnKey = 'tenThousands' | 'thousands' | 'hundreds' | 'tens' | 'ones';
const COLUMN_META: Record<ColumnKey, {
  label: string;
  short: string;
  value: number;
  color: string;
  ring: string;
}> = {
  tenThousands: {
    label: 'Tienduizendtal',
    short: 'TD',
    value: 10000,
    color: 'bg-indigo-600',
    ring: 'ring-indigo-300'
  },
  thousands: {
    label: 'Duizendtal',
    short: 'D',
    value: 1000,
    color: 'bg-slate-700',
    ring: 'ring-slate-400'
  },
  hundreds: {
    label: 'Honderdtal',
    short: 'H',
    value: 100,
    color: 'bg-orange-400',
    ring: 'ring-orange-300'
  },
  tens: {
    label: 'Tiental',
    short: 'T',
    value: 10,
    color: 'bg-emerald-500',
    ring: 'ring-emerald-300'
  },
  ones: {
    label: 'Eenheid',
    short: 'E',
    value: 1,
    color: 'bg-sky-400',
    ring: 'ring-sky-300'
  }
};
const SIZE_MAP = {
  sm: {
    unit: 10,
    gap: 2,
    columnWidth: 56
  },
  md: {
    unit: 14,
    gap: 3,
    columnWidth: 72
  },
  lg: {
    unit: 18,
    gap: 4,
    columnWidth: 92
  }
} as const;
/**
 * PlaceValueBlocks — MAB-blokjes / positiewaarde visual
 *
 * Renders concrete base-10 blocks for thousands (cube), hundreds (flat),
 * tens (rod) and ones (unit square). Used both as a stimulus visual and
 * inside the interactive place_value_build renderer.
 */
export const PlaceValueBlocks: React.FC<PlaceValueBlocksProps> = ({
  tenThousands = 0,
  thousands = 0,
  hundreds = 0,
  tens = 0,
  ones = 0,
  showLabels = true,
  showTotal = true,
  highlightColumn = null,
  maxBlocksPerColumn = 9,
  size = 'md'
}) => {
  const {
    unit,
    gap,
    columnWidth
  } = SIZE_MAP[size];
  const clamp = (n: number) => Math.max(0, Math.min(maxBlocksPerColumn, n));
  const counts: Record<ColumnKey, number> = {
    tenThousands: clamp(tenThousands),
    thousands: clamp(thousands),
    hundreds: clamp(hundreds),
    tens: clamp(tens),
    ones: clamp(ones)
  };
  // Hide the tenThousands column entirely when zero AND no caller is asking
  // for it — keeps the existing 4-column layout intact for typical 4-digit
  // place-value questions.
  const showTenThousands = tenThousands > 0 || highlightColumn === 'tenThousands';
  const total = counts.tenThousands * 10000 + counts.thousands * 1000 + counts.hundreds * 100 + counts.tens * 10 + counts.ones;
  const renderBlock = (column: ColumnKey, index: number) => {
    const meta = COLUMN_META[column];
    const delay = index * 0.04;
    const baseMotion = {
      initial: {
        opacity: 0,
        y: -8,
        scale: 0.8
      },
      animate: {
        opacity: 1,
        y: 0,
        scale: 1
      },
      transition: {
        delay,
        duration: 0.25,
        ease: 'easeOut' as const
      }
    };
    if (column === 'tenThousands' || column === 'thousands') {
      // Isometric cube — 3D-ish via two stacked diamonds + front face.
      // tenThousands uses the same cube glyph as thousands, but with its
      // own color (indigo) so the place value is unambiguous.
      const s = unit * 3.5;
      return <motion.div key={`${column}-${index}`} {...baseMotion} className="relative" style={{
        width: s,
        height: s
      }} aria-hidden data-id="element-13271">
          <div className={`absolute inset-0 ${meta.color} rounded-sm shadow-sm`} data-id="element-13272" />
          <div className="absolute left-0 right-0 top-0 h-1.5 bg-white/30 rounded-t-sm" style={{
          transform: 'skewX(-20deg) translateX(2px)'
        }} data-id="element-13273" />
          <div className="absolute inset-0 border border-white/20 rounded-sm" data-id="element-13274" />
        </motion.div>;
    }
    if (column === 'hundreds') {
      // Flat plate — 10x10 grid
      const isCompact = counts.hundreds >= 3;
      const s = isCompact ? unit * 3 : unit * 4.5;
      return <motion.div key={`${column}-${index}`} {...baseMotion} className={`relative ${meta.color} rounded-sm shadow-sm`} style={{
        width: s,
        height: s
      }} aria-hidden data-id="element-13275">
          <div className="absolute inset-0 grid grid-cols-10 grid-rows-10 gap-[0.5px] p-[0.5px]" data-id="element-13276">
            {Array.from({
            length: 100
          }).map((_, i) => <div key={i} className="bg-white/20 rounded-[0.5px]" data-id="element-13277" />)}
          </div>
        </motion.div>;
    }
    if (column === 'tens') {
      // Vertical bar / rod
      return <motion.div key={`${column}-${index}`} {...baseMotion} className={`relative ${meta.color} rounded-sm shadow-sm`} style={{
        width: unit,
        height: unit * 3
      }} aria-hidden data-id="element-13278">
          <div className="absolute inset-x-0 top-1/3 h-px bg-white/25" data-id="element-13279" />
          <div className="absolute inset-x-0 top-2/3 h-px bg-white/25" data-id="element-13280" />
        </motion.div>;
    }
    // ones — unit square
    return <motion.div key={`${column}-${index}`} {...baseMotion} className={`${meta.color} rounded-sm shadow-sm`} style={{
      width: unit,
      height: unit
    }} aria-hidden data-id="element-13281" />;
  };
  const renderColumn = (column: ColumnKey) => {
    const meta = COLUMN_META[column];
    const count = counts[column];
    const isHighlighted = highlightColumn === column;
    return <div key={column} className={`flex flex-col items-center ${isHighlighted ? `ring-2 ${meta.ring} rounded-xl p-2 -m-2` : ''}`} style={{
      minWidth: columnWidth
    }} data-id="element-13282">
        {showLabels && <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500 mb-2" data-id="element-13283">
            {meta.label}
          </div>}

        <div className="flex flex-col-reverse items-center justify-start min-h-[120px]" style={{
        gap
      }} role="img" aria-label={`${count} ${meta.label.toLowerCase()}`} data-id="element-13284">
          {count === 0 ? <div className="border-2 border-dashed border-slate-200 rounded-md" style={{
          width: unit * 2,
          height: unit * 2
        }} data-id="element-13285" /> : Array.from({
          length: count
        }).map((_, i) => renderBlock(column, i))}
        </div>

        <div className="mt-3 flex flex-col items-center" data-id="element-13286">
          <span className="text-xl font-black tabular-nums text-slate-800" data-id="element-13287">
            {count}
          </span>
          <span className="text-[10px] font-semibold text-slate-400" data-id="element-13288">
            × {meta.value.toLocaleString('nl-NL')}
          </span>
        </div>
      </div>;
  };
  return <div className="flex flex-col items-center w-full" aria-label={`Positiewaarde-blokken, totaal ${total.toLocaleString('nl-NL')}`} data-id="element-13289">
      <div className="flex items-end justify-center gap-3 sm:gap-6" data-id="element-13290">
        {(Object.keys(COLUMN_META) as ColumnKey[]).filter(col => col !== 'tenThousands' || showTenThousands).map(renderColumn)}
      </div>

      {showTotal && <div className="mt-6 px-4 py-2 bg-slate-50 border border-slate-200 rounded-lg" data-id="element-13291">
          <span className="text-xs font-bold uppercase tracking-wider text-slate-500 mr-2" data-id="element-13292">
            Totaal
          </span>
          <span className="text-2xl font-black font-mono text-slate-900 tabular-nums" data-id="element-13293">
            {total.toLocaleString('nl-NL')}
          </span>
        </div>}
    </div>;
};